import React, { useState } from 'react';
import { t } from '../../../locales/translate';
import { Card } from '../../../components/Card/Card';
import { PermissionButton } from '../../../components/Button/PermissionButton';
import { Modal } from '../../../components/Modal/Modal';
import { PricingCategoryForm } from './PricingCategoryForm';
import { SortableList } from '../../../components/SortableList/SortableList';
import { graphql } from '../../../generated';
import { useSmartConfirmation } from '../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { useMutationHandler, useQueryHandler } from '../../../model/graphql';
import { pricingCategoryColors } from '../../../model/seatmap/helper';

const VENUE_LAYOUT_PRICING_CATEGORIES = graphql(`
  query VenueLayoutPricingCategories($id: ID!) {
    venueLayout(id: $id) {
      permissions {
        id
        createPricingCategory
      }
      pricingCategories {
        id
        name
        seatsCountInVenueLayout
        venueSectionsCountInVenueLayout
        permissions {
          id
          destroy
          update
        }
      }
    }
  }
`);

const DESTROY_PRICING_CATEGORY = graphql(`
  mutation DestroyPricingCategory($id: ID!) {
    destroyPricingCategory(input: { id: $id }) {
      venueLayout {
        id
        pricingCategories {
          id
        }
      }
    }
  }
`);

interface Props {
  venueLayoutId: string;
  onChange: (action: 'create' | 'delete' | 'edit') => void;
}

export function PricingCategoriesCard({ venueLayoutId, onChange }: Props): JSX.Element {
  const { data, loading, refetch } = useQueryHandler(VENUE_LAYOUT_PRICING_CATEGORIES, {
    variables: { id: venueLayoutId },
  });
  const venueLayout = data?.venueLayout;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [categoryIdToEdit, setCategoryIdToEdit] = useState<string | undefined>(undefined);
  const [destroyPricingCategory] = useMutationHandler(DESTROY_PRICING_CATEGORY);

  const confirmDestroyPricingCategory = useSmartConfirmation();

  return (
    <Card
      loading={loading}
      title={t.model.pricingCategory._plural}
      actions={
        <>
          <PermissionButton
            name="plus"
            model={venueLayout}
            permission="createPricingCategory"
            onClick={() => setShowCreateModal(true)}
          />
        </>
      }
    >
      <SortableList
        items={venueLayout?.pricingCategories?.map((i) => ({
          ...i,
          edit: 'edit',
        }))}
        columns={[
          {
            dataKey: 'id',
            label: '',
            width: 50,
            cellRenderer: (_, index) => (
              <div className="pricing-categories-card__color-style">
                <svg width={12} height={12}>
                  <circle fill={pricingCategoryColors[index]} cx={6} cy={6} r={6} />
                </svg>
              </div>
            ),
          },
          {
            dataKey: 'name',
            label: t.model.pricingCategory.name,
          },
          {
            dataKey: 'seatsCountInVenueLayout',
            label: t.model.pricingCategory.seatsCount,
          },
          {
            dataKey: 'venueSectionsCountInVenueLayout',
            label: t.model.pricingCategory.venueSectionsCount,
          },
          {
            dataKey: 'edit',
            cellRenderer: (category) => (
              <div className="card__actions">
                <PermissionButton
                  name="edit"
                  model={category}
                  permission="update"
                  onClick={() => setCategoryIdToEdit(category.id)}
                  tooltip={t.actions.edit(t.model.pricingCategory._name)}
                />
                <PermissionButton
                  name="trash"
                  model={category}
                  permission="destroy"
                  onClick={() => {
                    confirmDestroyPricingCategory({
                      title: t.general.confirmDelete(t.model.pricingCategory._name),
                      action: async () => {
                        await destroyPricingCategory({ variables: { id: category.id } });
                        onChange('delete');
                        refetch();
                      },
                    });
                  }}
                  tooltip={t.actions.delete(t.model.pricingCategory._name)}
                  hideLock={true}
                />
              </div>
            ),
          },
        ]}
      />
      <Modal open={showCreateModal} title={t.actions.create(t.model.pricingCategory._name)} size="xs">
        <PricingCategoryForm
          venueLayoutId={venueLayoutId}
          onCreate={() => {
            onChange('create');
            refetch();
          }}
          onClose={() => setShowCreateModal(false)}
        />
      </Modal>
      <Modal open={!!categoryIdToEdit} title={t.actions.edit(t.model.pricingCategory._name)} size="xs">
        <PricingCategoryForm
          venueLayoutId={venueLayoutId}
          pricingCategoryId={categoryIdToEdit}
          onEdit={() => {
            onChange('edit');
            refetch();
          }}
          onClose={() => setCategoryIdToEdit(undefined)}
        />
      </Modal>
    </Card>
  );
}
